import { business } from '@/config/business';
import { buildSchemaPayload, OG_IMAGE_ALT, OG_IMAGE_PATH } from '@/config/schema';
import { absoluteUrl, getSiteUrl } from '@/lib/siteUrl';

export { OG_IMAGE_ALT, OG_IMAGE_PATH };

export type ApplySeoArgs = {
  title: string;
  description: string;
  path?: string;
  noindex?: boolean;
};

function ensureMeta(by: 'name' | 'property', key: string): HTMLMetaElement {
  const selector = by === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(by, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMetaName(name: string, content: string) {
  ensureMeta('name', name).setAttribute('content', content);
}

function setMetaProperty(property: string, content: string) {
  ensureMeta('property', property).setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setHreflang(hreflang: string, href: string) {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function pageUrl(path: string): string {
  const base = getSiteUrl();
  if (!path || path === '/') return base;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export function applySeo({ title, description, path = '/', noindex = false }: ApplySeoArgs) {
  const pathname = path.startsWith('/') ? path : `/${path}`;
  const canonical = pageUrl(pathname);
  const imageAbs = absoluteUrl(OG_IMAGE_PATH);
  const site = getSiteUrl();

  document.title = title;
  setMetaName('description', description);

  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1';
  setMetaName('robots', robotsContent);

  setMetaProperty('og:type', 'website');
  setMetaProperty('og:locale', 'en_US');
  setMetaProperty('og:site_name', business.brandName);
  setMetaProperty('og:title', title);
  setMetaProperty('og:description', description);
  setMetaProperty('og:url', canonical);
  setMetaProperty('og:image', imageAbs);
  setMetaProperty('og:image:width', '1200');
  setMetaProperty('og:image:height', '630');
  setMetaProperty('og:image:alt', OG_IMAGE_ALT);

  setMetaName('twitter:card', 'summary_large_image');
  setMetaName('twitter:title', title);
  setMetaName('twitter:description', description);
  setMetaName('twitter:image', imageAbs);
  setMetaName('twitter:image:alt', OG_IMAGE_ALT);

  setCanonical(canonical);
  setHreflang('en', canonical);
  setHreflang('x-default', canonical);

  const payload = buildSchemaPayload({
    path: pathname,
    title,
    description,
    siteUrl: site,
    imageUrl: imageAbs,
  });

  const existing = document.getElementById('seo-jsonld');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = 'seo-jsonld';
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(payload);
  document.head.appendChild(script);
}
